import { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Overlay";
import { Notice } from "../ui/Notice";
import { useT } from "../../lib/i18n";
import { fetchTrash, restoreVersionApi, type TrashRow } from "../../lib/api";

const when = (ms: number) => new Date(ms).toLocaleString();

/**
 * 回收站：7 天内删掉的草稿，带谁删的、什么时候清掉、谁改过。点「恢复」变回草稿，内容原样。
 * 2026-10-21 1201 的草稿 v129 被删，里面别人一下午的改动跟着没了——删除改成进回收站之后，
 * 这种情况在这里点一下就回来。
 */
export function TrashDialog({ onClose, onRestored }: { onClose: () => void; onRestored: (configVersion: number) => void }) {
  const t = useT();
  const [rows, setRows] = useState<TrashRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    fetchTrash().then((r) => { if (alive) setRows(r); }, (e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, []);
  const restore = async (v: number) => {
    setBusy(v);
    try {
      await restoreVersionApi(v);
      onRestored(v);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };
  return (
    <Modal title={t("ver.trash")} width={560} onClose={onClose}
      footer={<div className="flex justify-end"><Button size="sm" onClick={onClose}>{t("log.close")}</Button></div>}>
      <div className="mb-3 text-sm text-ink-secondary">{t("ver.trashHint")}</div>
      {err && <Notice tone="danger">{err}</Notice>}
      {rows == null && !err && <div className="py-6 text-center text-sm text-ink-placeholder">{t("ver.working")}</div>}
      {rows && !rows.length && <div className="py-6 text-center text-sm text-ink-placeholder">{t("ver.trashEmpty")}</div>}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-line-lighter">
          {rows.map((r) => (
            <li key={r.configVersion} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0 text-sm">
                <div className="font-semibold">v{r.configVersion}{r.note ? <span className="ml-2 font-normal text-ink-secondary">{r.note}</span> : null}</div>
                <div className="mt-1 text-xs text-ink-secondary">{t("ver.trashBy", { who: r.trashedBy || "—", at: when(r.trashedAtMs) })}</div>
                <div className="mt-0.5 text-xs text-ink-secondary">
                  {r.editors.length
                    ? t("ver.trashEditors", { list: r.editors.map((e) => t("ver.trashEditor", { who: e.actor || "—", n: String(e.n) })).join("、") })
                    : t("ver.trashNoEdits")}
                </div>
                <div className="mt-0.5 text-xs text-ink-placeholder">{t("ver.trashPurgeAt", { at: when(r.purgeAtMs) })}</div>
              </div>
              <Button size="sm" disabled={busy != null} onClick={() => { void restore(r.configVersion); }}>
                {busy === r.configVersion ? t("ver.working") : t("ver.restore")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
